import React, { useRef, useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import useGlobalState, { g_user, API_Root } from '../../globalState';
import { renderTripRoute } from '../trip/tripRoute';
import { vehicleName } from '../trip/vehicleName';

const vehicleLabel = (v) => vehicleName(v, 'Unspecified vehicle');

const fromNow = (dt) => {
   if (!dt) {
      return '';
   }
   const d = new Date(dt);
   const diffMs = Date.now() - d.getTime();
   const absMin = Math.round(Math.abs(diffMs) / 60000);
   if (absMin < 1) {
      return 'just now';
   }
   if (absMin < 60) {
      return diffMs < 0 ? `in ${absMin} min` : `${absMin} min ago`;
   }
   const absHr = Math.round(absMin / 60);
   if (absHr < 24) {
      return diffMs < 0 ? `in ${absHr} hr` : `${absHr} hr ago`;
   }
   const absDay = Math.round(absHr / 24);
   return diffMs < 0 ? `in ${absDay} day${absDay === 1 ? '' : 's'}` : `${absDay} day${absDay === 1 ? '' : 's'} ago`;
};

const formatDate = (dt) => {
   if (!dt) {
      return '';
   }
   return new Date(dt).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
   });
};

export const DriverHome = () => {
   const [toDo, setToDo] = useState(null);
   const [availableCount, setAvailableCount] = useState(0);
   const [loading, setLoading] = useState(true);
   const [user,] = useGlobalState(g_user);
   const toast = useRef(null);
   const history = useHistory();

   const load = () => {
      if (!user?.user?.id) {
         return;
      }
      const uid = user.user.id;
      axios.get(`${API_Root}api/trip/GetUserToDo?userId=${uid}`).then((values) => {
         setToDo(values.data || null);
         setLoading(false);
      }).catch(() => {
         setLoading(false);
         toast.current?.show({
            severity: 'error',
            summary: 'Could not load home',
            detail: 'Please try refresh.',
            life: 4000
         });
      });
      axios.get(`${API_Root}api/trip/GetAvailableTripsForUser?userId=${uid}`).then((values) => {
         setAvailableCount((values.data || []).length);
      }).catch(() => { });
   };

   useEffect(() => {
      load();
      const interval = setInterval(() => load(), 60000);
      return () => clearInterval(interval);
   }, [user?.user?.id]);

   const openTrip = (id, vehicleId) => {
      history.push(vehicleId ? `/my-trips/${id}?vehicleId=${vehicleId}` : `/my-trips/${id}`);
   };

   const vehicleRows = (rows) => {
      const out = [];
      (rows || []).forEach((r) => {
         (r.TripVehicles || []).forEach((v) => {
            out.push({ trip: r, vehicle: v });
         });
      });
      return out;
   };

   const section = (title, rows, tone) => {
      const items = vehicleRows(rows);
      if (!items.length) {
         return null;
      }
      return (
         <div className="todo-section" key={title}>
            <div className="todo-section-head">
               <h3>{title}</h3>
               <Tag value={String(items.length)} severity={tone}></Tag>
            </div>
            {items.map(({ trip, vehicle }) => (
               <div
                  key={`${title}-${trip.id}-${vehicle.id}`}
                  className="job-card job-card-clickable mb-2"
                  onClick={() => openTrip(trip.id, vehicle.id)}>
                  <div className="job-card-when">{formatDate(trip.departDateTimeStart)} ({fromNow(trip.departDateTimeStart)})</div>
                  <div className="job-card-route">{renderTripRoute(trip, [vehicle])}</div>
                  <div className="job-card-meta">
                     {vehicleLabel(vehicle)}
                  </div>
               </div>
            ))}
         </div>
      );
   };

   const assignSection = () => {
      const rows = toDo?.NeedsAssign;
      if (!rows || rows.length === 0) {
         return null;
      }
      return (
         <div className="todo-section">
            <div className="todo-section-head">
               <h3>Needs a driver</h3>
               <Tag value={String(rows.length)} severity="danger"></Tag>
            </div>
            {rows.map((r) => (
               <div
                  key={`assign-${r.id}`}
                  className="job-card job-card-clickable mb-2"
                  onClick={() => history.push('/trip-list')}>
                  <div className="job-card-when">{formatDate(r.departDateTimeStart)} ({fromNow(r.departDateTimeStart)})</div>
                  <div className="job-card-route">{renderTripRoute(r, r.TripVehicles)}</div>
               </div>
            ))}
         </div>
      );
   };

   const hasDriverItems = toDo && (
      (toDo.InProgress?.length > 0) ||
      (toDo.StartingSoon?.length > 0) ||
      (toDo.OverdueStart?.length > 0) ||
      (toDo.NeedsConfirm?.length > 0)
   );
   const hasAssignItems = toDo?.NeedsAssign?.length > 0;
   const hasAnything = hasDriverItems || hasAssignItems || availableCount > 0;

   return (
      <>
         <Toast ref={toast} />
         <div className="jobs-page">
            <div className="jobs-page-header">
               <div>
                  <h2 className="jobs-page-title">Home</h2>
                  <p className="jobs-page-meta">
                     {loading ? 'Loading action items…' : 'Trips that need your attention'}
                  </p>
               </div>
               <Button
                  label="Refresh"
                  icon="pi pi-refresh"
                  onClick={() => load()}
                  disabled={loading}
               />
            </div>

            {loading && !toDo && (
               <div className="jobs-empty">
                  <i className="pi pi-spin pi-spinner text-3xl mb-3"></i>
                  <p>Loading action items…</p>
               </div>
            )}

            {!loading && !hasAnything && (
               <div className="jobs-empty">
                  <i className="pi pi-check-circle text-5xl mb-3"></i>
                  <h3>No action items right now</h3>
                  <p>Claim a trip order when one is available, or open My Trips to review assignments.</p>
                  <div className="flex justify-content-center gap-2 mt-3">
                     <Button label="Trip orders" icon="pi pi-car" onClick={() => history.push('/available-trips')} />
                     <Button label="My trips" icon="pi pi-list" outlined onClick={() => history.push('/my-trips')} />
                  </div>
               </div>
            )}

            {availableCount > 0 && (
               <div className="todo-section">
                  <div className="todo-section-head">
                     <h3>Open trip orders</h3>
                     <Tag value={String(availableCount)}></Tag>
                  </div>
                  <div className="job-card">
                     <p className="job-card-hint mt-0">
                        {availableCount === 1 ? '1 trip order is available to claim.' : `${availableCount} trip orders are available to claim.`}
                     </p>
                     <Button
                        label="View trip orders"
                        icon="pi pi-car"
                        size="small"
                        className="mt-2"
                        onClick={() => history.push('/available-trips')}
                     />
                  </div>
               </div>
            )}

            {toDo && (
               <>
                  {section('Confirmation needed', toDo.NeedsConfirm, 'warning')}
                  {section('Departure overdue', toDo.OverdueStart, 'danger')}
                  {section('Starting soon', toDo.StartingSoon, 'info')}
                  {section('Trips in progress', toDo.InProgress, 'success')}
                  {assignSection()}
               </>
            )}
         </div>
      </>
   );
};
